import { PayloadAction } from "@reduxjs/toolkit";
import { ColumnSort, SortingState } from "@tanstack/react-table";
import {
  ColumnOrderState,
  ColumnSizingState,
  SortDirection,
  VisibilityState,
} from "@tanstack/table-core";
import moment from "moment";
import { all, call, put, select, take, takeLatest } from "redux-saga/effects";
import { AppState } from "../../../store";
import fetchAPI, { ApiResponse, HttpMethod } from "../../../utils/api";
import { Nullable } from "../../../utils/types";
import bookingFilterSlice from "../../booking-filters/store/slice";
import bookingSettingSlice, {
  PerPage,
  TableSize,
} from "../../booking-settings/store/slice";
import cargoTypeSlice, { CargoType } from "../../cargo-types/store/slice";
import loginPageSlice from "../../login-page/store/slice";
import { Route } from "../../routes/store/slice";
import statusSlice, { Status } from "../../statuses/store/slice";
import userFiltersSlice, { UserFilters } from "../../user-filters/store/slice";
import { User } from "../../users/store/slice";
import vehicleTypeSlice, { VehicleType } from "../../vehicle-types/store/slice";
import trailerTypeSlice, { TrailerType } from "../../trailer-types/store/slice";

export type UserSettings = Nullable<{
  status_ids: number[];
  vehicle_type_ids: number[];
  trailer_type_ids: number[];
  cargo_type_ids: number[];
  is_archived: boolean;
  is_created_by_me: boolean;
  search_query: string;
  sort_by: string;
  sort_direction: SortDirection;
  per_page: PerPage;
  table_size: TableSize;
  column_visibility: VisibilityState;
  column_size: ColumnSizingState;
  column_order: ColumnOrderState;
  route_ids: number[];
  departure_from: string;
  departure_to: string;
  arrival_from: string;
  arrival_to: string;
  customer_ids: number[];
}>;

type SettingsResponse = ApiResponse<{ data: UserSettings }>;

function* userSettingsSaga() {
  yield takeLatest(loginPageSlice.actions.setLoggedIn, handleFetch);
  yield takeLatest(loginPageSlice.actions.setLoggedOut, handleReset);

  yield takeLatest(
    bookingSettingSlice.actions.setColumnOrder,
    function* ({ payload }: PayloadAction<ColumnOrderState>) {
      yield call(handleUpdate, { column_order: payload });
    }
  );
  yield takeLatest(
    bookingSettingSlice.actions.setColumnSizing,
    function* ({ payload }: PayloadAction<ColumnSizingState>) {
      yield call(handleUpdate, { column_size: payload });
    }
  );
  yield takeLatest(
    bookingSettingSlice.actions.setColumnVisibility,
    function* ({ payload }: PayloadAction<VisibilityState>) {
      const column_visibility: VisibilityState | undefined = yield select(
        (state: AppState) => state.bookingSettings.columnVisibility
      );

      yield call(handleUpdate, { column_visibility });
    }
  );
  yield takeLatest(
    bookingSettingSlice.actions.setTableSize,
    function* ({ payload }: PayloadAction<TableSize>) {
      yield call(handleUpdate, { table_size: payload });
    }
  );
  yield takeLatest(
    bookingSettingSlice.actions.setPerPage,
    function* ({ payload }: PayloadAction<PerPage>) {
      yield call(handleUpdate, { per_page: payload });
    }
  );
  yield takeLatest(
    bookingFilterSlice.actions.setSearchFilter,
    function* ({ payload }: PayloadAction<string>) {
      yield call(handleUpdate, { search_query: payload });
    }
  );
  yield takeLatest(bookingFilterSlice.actions.toggleArchived, function* () {
    const isArchived: boolean = yield select(
      (state: AppState) => state.bookingFilters.archived
    );

    yield call(handleUpdate, { is_archived: isArchived });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleCreatedByMe, function* () {
    const isCreatedByMe: boolean = yield select(
      (state: AppState) => state.bookingFilters.createdByMe
    );

    yield call(handleUpdate, { is_created_by_me: isCreatedByMe });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleStatus, function* () {
    const selectedStatuses: Status[] = yield select(
      (state: AppState) => state.bookingFilters.selectedStatuses
    );

    yield call(handleUpdate, {
      status_ids: selectedStatuses.map((status) => status.id),
    });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleVehicleType, function* () {
    const selectedVehicleTypes: VehicleType[] = yield select(
      (state: AppState) => state.bookingFilters.selectedVehicleTypes
    );

    yield call(handleUpdate, {
      vehicle_type_ids: selectedVehicleTypes.map((type) => type.id),
    });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleTrailerType, function* () {
    const selectedTrailerTypes: TrailerType[] = yield select(
      (state: AppState) => state.bookingFilters.selectedTrailerTypes
    );

    yield call(handleUpdate, {
      trailer_type_ids: selectedTrailerTypes.map((type) => type.id),
    });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleCargoType, function* () {
    const selectedCargoTypes: CargoType[] = yield select(
      (state: AppState) => state.bookingFilters.selectedCargoTypes
    );

    yield call(handleUpdate, {
      cargo_type_ids: selectedCargoTypes.map((type) => type.id),
    });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleRoute, function* () {
    const selectedRoutes: Route[] = yield select(
      (state: AppState) => state.bookingFilters.selectedRoutes
    );

    yield call(handleUpdate, {
      route_ids: selectedRoutes.map((route) => route.id),
    });
  });
  yield takeLatest(bookingFilterSlice.actions.toggleCustomer, function* () {
    const selectedCustomers: User[] = yield select(
      (state: AppState) => state.bookingFilters.selectedCustomers
    );

    yield call(handleUpdate, {
      customer_ids: selectedCustomers.map((customer) => customer.id),
    });
  });
  yield takeLatest(bookingFilterSlice.actions.setCustomers, function* () {
    const selectedCustomers: User[] = yield select(
      (state: AppState) => state.bookingFilters.selectedCustomers
    );

    yield call(handleUpdate, {
      customer_ids: selectedCustomers.map((customer) => customer.id),
    });
  });
  yield takeLatest(bookingSettingSlice.actions.setSorting, function* () {
    const sorting: SortingState = yield select(
      (state: AppState) => state.bookingSettings.sorting
    );

    yield call(handleUpdate, {
      sort_by: sorting.length > 0 ? sorting[0].id : null,
      sort_direction:
        sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : null,
    });
  });
  yield takeLatest(bookingFilterSlice.actions.setDepartureTo, function* () {
    const departureFrom: string | null = yield select(
      (state: AppState) => state.bookingFilters.departureFrom
    );
    const departureTo: string | null = yield select(
      (state: AppState) => state.bookingFilters.departureTo
    );
    yield call(handleUpdate, {
      departure_from: departureFrom
        ? moment(departureFrom).format("YYYY-MM-DD")
        : null,
      departure_to: departureTo
        ? moment(departureTo).format("YYYY-MM-DD")
        : null,
    });
  });
  yield takeLatest(bookingFilterSlice.actions.setArrivalTo, function* () {
    const arrivalFrom: string | null = yield select(
      (state: AppState) => state.bookingFilters.arrivalFrom
    );
    const arrivalTo: string | null = yield select(
      (state: AppState) => state.bookingFilters.arrivalTo
    );
    yield call(handleUpdate, {
      arrival_from: arrivalFrom
        ? moment(arrivalFrom).format("YYYY-MM-DD")
        : null,
      arrival_to: arrivalTo ? moment(arrivalTo).format("YYYY-MM-DD") : null,
    });
  });
}

function* handleReset() {
  yield put(bookingFilterSlice.actions.reset());
  yield put(bookingSettingSlice.actions.reset());
  yield put(statusSlice.actions.reset());
  yield put(vehicleTypeSlice.actions.reset());
  yield put(trailerTypeSlice.actions.reset());
  yield put(cargoTypeSlice.actions.reset());
  yield put(userFiltersSlice.actions.reset());
}

function* handleFetch() {
  const tasks: any[] = yield all([
    call(fetchAPI, "/user/settings"),
    take(statusSlice.actions.setData),
    take(vehicleTypeSlice.actions.setData),
    take(trailerTypeSlice.actions.setData),
    take(cargoTypeSlice.actions.setData),
    take(userFiltersSlice.actions.setData),
  ]);

  const { data }: SettingsResponse = tasks[0];

  if (data) {
    yield all([
      call(setIsArchived, data),
      call(setIsCreatedByMe, data),
      call(setSearchFilter, data),
      call(setPerPage, data),
      call(setTableSize, data),
      call(setColumnVisibility, data),
      call(setColumnOrder, data),
      call(setColumnSizing, data),
      call(setSorting, data),
      call(setStatuses, data),
      call(setVehicleTypes, data),
      call(setTrailerTypes, data),
      call(setCargoTypes, data),
      call(setRoutes, data),
      call(setCustomers, data),
      call(setDepartureFrom, data),
      call(setDepartureTo, data),
      call(setArrivalFrom, data),
      call(setArrivalTo, data),
    ]);
  }

  yield put(bookingFilterSlice.actions.setReady(true));
  yield put(bookingSettingSlice.actions.setReady(true));
}

function* handleUpdate(data: Partial<UserSettings>) {
  const settingsReady: boolean = yield select(
    (state: AppState) => state.bookingSettings.isReady
  );
  const filtersReady: boolean = yield select(
    (state: AppState) => state.bookingFilters.isReady
  );

  if (settingsReady && filtersReady) {
    yield call(fetchAPI, `/user/settings`, {
      method: HttpMethod.PUT,
      body: JSON.stringify(data),
    });
  }
}

function* setIsArchived(data: UserSettings) {
  if (data.is_archived !== null) {
    yield put(bookingFilterSlice.actions.setIsArchived(data.is_archived));
  }
}

function* setIsCreatedByMe(data: UserSettings) {
  if (data.is_created_by_me !== null) {
    yield put(
      bookingFilterSlice.actions.setIsCreatedByMe(data.is_created_by_me)
    );
  }
}

function* setSearchFilter(data: UserSettings) {
  if (data.search_query !== null) {
    yield put(bookingFilterSlice.actions.setSearchFilter(data.search_query));
  }
}

function* setPerPage(data: UserSettings) {
  if (data.per_page !== null) {
    yield put(bookingSettingSlice.actions.setPerPage(data.per_page));
  }
}

function* setTableSize(data: UserSettings) {
  if (data.table_size !== null) {
    yield put(bookingSettingSlice.actions.setTableSize(data.table_size));
  }
}

function* setColumnVisibility(data: UserSettings) {
  if (data.column_visibility !== null) {
    yield put(
      bookingSettingSlice.actions.setColumnVisibility(data.column_visibility)
    );
  }
}

function* setColumnOrder(data: UserSettings) {
  if (data.column_order !== null) {
    yield put(bookingSettingSlice.actions.setColumnOrder(data.column_order));
  }
}

function* setColumnSizing(data: UserSettings) {
  if (data.column_size !== null) {
    yield put(bookingSettingSlice.actions.setColumnSizing(data.column_size));
  }
}

function* setSorting(data: UserSettings) {
  if (data.sort_by !== null && data.sort_direction !== null) {
    const sort: ColumnSort = {
      id: data.sort_by,
      desc: data.sort_direction === "desc",
    };

    yield put(bookingSettingSlice.actions.setSorting([sort]));
  }
}

function* setStatuses(data: UserSettings) {
  const ids = data.status_ids;

  if (ids !== null) {
    const statuses: Status[] = yield select(
      (state: AppState) => state.statuses.data
    );

    if (statuses.length > 0 && ids.length > 0) {
      yield put(
        bookingFilterSlice.actions.setStatuses(
          statuses.filter((status) => ids.includes(status.id))
        )
      );
    }
  }
}

function* setVehicleTypes(data: UserSettings) {
  const ids = data.vehicle_type_ids;

  if (ids !== null) {
    const vehicleTypes: VehicleType[] = yield select(
      (state: AppState) => state.vehicleTypes.data
    );

    if (vehicleTypes.length > 0 && ids.length > 0) {
      yield put(
        bookingFilterSlice.actions.setVehicleTypes(
          vehicleTypes.filter((type) => ids.includes(type.id))
        )
      );
    }
  }
}

function* setTrailerTypes(data: UserSettings) {
  const ids = data.trailer_type_ids;

  if (ids !== null) {
    const trailerTypes: TrailerType[] = yield select(
      (state: AppState) => state.trailerTypes.data
    );

    if (trailerTypes.length > 0 && ids.length > 0) {
      yield put(
        bookingFilterSlice.actions.setTrailerTypes(
          trailerTypes.filter((type) => ids.includes(type.id))
        )
      );
    }
  }
}

function* setCargoTypes(data: UserSettings) {
  const ids = data.cargo_type_ids;

  if (ids !== null) {
    const cargoTypes: CargoType[] = yield select(
      (state: AppState) => state.cargoTypes.data
    );

    if (cargoTypes.length > 0 && ids.length > 0) {
      yield put(
        bookingFilterSlice.actions.setCargoTypes(
          cargoTypes.filter((type) => ids.includes(type.id))
        )
      );
    }
  }
}

function* setCustomers(data: UserSettings) {
  const ids = data.customer_ids;

  if (ids !== null) {
    const personalizedFilters: UserFilters | null = yield select(
      (state: AppState) => state.userFilters.data
    );

    if (
      personalizedFilters &&
      personalizedFilters.customers.length > 0 &&
      ids.length > 0
    ) {
      yield put(
        bookingFilterSlice.actions.setCustomers(
          personalizedFilters.customers.filter((customer) =>
            ids.includes(customer.id)
          )
        )
      );
    }
  }
}

function* setRoutes(data: UserSettings) {
  const ids = data.route_ids;

  if (ids !== null) {
    const personalizedFilters: UserFilters | null = yield select(
      (state: AppState) => state.userFilters.data
    );

    if (
      personalizedFilters &&
      personalizedFilters.routes.length > 0 &&
      ids.length > 0
    ) {
      yield put(
        bookingFilterSlice.actions.setRoutes(
          personalizedFilters.routes.filter((route) => ids.includes(route.id))
        )
      );
    }
  }
}

function* setDepartureFrom(data: UserSettings) {
  if (data.departure_from !== null) {
    yield put(
      bookingFilterSlice.actions.setDepartureFrom(
        moment(data.departure_from).toDate()
      )
    );
  }
}

function* setDepartureTo(data: UserSettings) {
  if (data.departure_to !== null) {
    yield put(
      bookingFilterSlice.actions.setDepartureTo(
        moment(data.departure_to).toDate()
      )
    );
  }
}

function* setArrivalFrom(data: UserSettings) {
  if (data.arrival_from !== null) {
    yield put(
      bookingFilterSlice.actions.setArrivalFrom(
        moment(data.arrival_from).toDate()
      )
    );
  }
}

function* setArrivalTo(data: UserSettings) {
  if (data.arrival_to !== null) {
    yield put(
      bookingFilterSlice.actions.setArrivalTo(moment(data.arrival_to).toDate())
    );
  }
}

export default userSettingsSaga;
